/* eslint-disable complexity */
/* eslint-disable max-statements */
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import Animator from '@starbucks-web/pattern-library/lib/components/animator';
import FieldBase from '@starbucks-web/pattern-library/lib/components/field-base';

import useMoveFocus from '../../product-details-page/main-content/product-options-fields/use-move-focus';
import { maximumModifierAmountSelector } from '../../../state/selectors/config';

import MinusButton from './minus-button';
import PlusButton from './plus-button';
import getProductLabel from './get-product-label';
import { commonMessages } from 'shared/app/messages';

const Stepper = ({
  className = '',
  currentQuantity = 0,
  defaultQuantity = 0,
  isDemystifyMenuPdpEnabled,
  inRecipe,
  isRemovable,
  onValueChange,
  productOptionForm,
  productOptionName = '',
  productOptionNumber,
  productOptionPrice,
  topLevelCategoryName,
  unit,
  useInactiveStyle,
  validationId,
}) => {
  const { formatMessage, formatNumber, formats } = useIntl();
  const formattedProductOptionPrice =
    isDemystifyMenuPdpEnabled && Math.abs(productOptionPrice) > 0
      ? formatNumber(productOptionPrice, formats.number.money)
      : '';

  const plusButtonRef = useRef(null);
  const minusButtonRef = useRef(null);
  const [modified, setModified] = useState(false);
  const modifiedFromDefault = inRecipe
    ? currentQuantity !== defaultQuantity
    : currentQuantity > 0;

  // if the productOption cannot be removed from the product,
  // we want the minus button to disappear when current quantity is 1, not 0.
  const shouldShowMinusButton = isRemovable
    ? currentQuantity > 0
    : currentQuantity > 1;

  const maximumModifierAmount = useSelector(maximumModifierAmountSelector);
  const maximumModifierAmountReached = currentQuantity >= maximumModifierAmount;

  useMoveFocus({ productOptionNumber, elementRef: plusButtonRef });

  useEffect(() => {
    // if either button disappears due to modified state,
    // move focus to other button
    if (!shouldShowMinusButton && modified) {
      return plusButtonRef?.current?.focus();
    }

    if (maximumModifierAmountReached) {
      return minusButtonRef?.current?.focus();
    }
  }, [currentQuantity]);

  const buttonClickHandler = () => {
    if (!modified) {
      setModified(true);
    }
  };

  const accessibleLabelId = `${productOptionName.replace(
    /\s+/g,
    ''
  )}HiddenLabel`;

  const productLabel = getProductLabel({
    currentQuantity,
    inRecipe,
    productOptionForm,
    productOptionName,
    unit,
  });

  const getA11yQuantityLabel = () => {
    const customizedIndicator = formatMessage(commonMessages.customized);
    if (!currentQuantity) {
      if (modifiedFromDefault) {
        return `${productLabel} ${formattedProductOptionPrice}, ${customizedIndicator}`;
      }
      return productLabel;
    }

    const baseMessage = `${currentQuantity} ${productLabel}`;
    if (modifiedFromDefault) {
      return `${baseMessage} ${formattedProductOptionPrice}, ${customizedIndicator}${
        maximumModifierAmountReached
          ? `, ${formatMessage(commonMessages.maximumReached)}`
          : ''
      }`;
    }

    return `${baseMessage}, ${formatMessage(commonMessages.default)}`;
  };

  return (
    <div
      aria-label={`${topLevelCategoryName ?? ''}, ${productLabel}`}
      className={className}
      data-e2e="stepper"
      role="group"
    >
      <FieldBase
        floatLabelActive
        floatLabelActiveText={topLevelCategoryName}
        floatLabelText={topLevelCategoryName}
        secondaryLabel={
          modifiedFromDefault ? formatMessage(commonMessages.customized) : null
        }
        useInactiveStyle={useInactiveStyle}
        validationId={validationId}
      >
        <Animator>
          <div aria-hidden className="text-sm flex-grow">
            <div className="flex justify-between items-center">
              <span>{productLabel}</span>
              {formattedProductOptionPrice && (
                <span className="mr2 text-xs color-black60">
                  {formattedProductOptionPrice}
                </span>
              )}
            </div>
          </div>
        </Animator>
        <div
          aria-live={modified ? 'polite' : 'off'}
          className="hiddenVisually"
          id={accessibleLabelId}
        >
          {/*
           * This widget is hard to make sense of with a screenreader,
           * so we aria-hide the label and current quantity,
           * instead supplying this visually hidden aria-live region.
           * This contains a string of text that makes
           * a more natural sounding announcement of the
           * current quantity of the item being incremented/decremented.
           */}
          {getA11yQuantityLabel()}
        </div>
        <div className="flex">
          {shouldShowMinusButton && (
            <MinusButton
              ariaDescribedBy={accessibleLabelId}
              currentQuantity={currentQuantity}
              modifiedFromDefault={modifiedFromDefault}
              onClick={buttonClickHandler}
              onValueChange={onValueChange}
              ref={minusButtonRef}
            />
          )}
          {currentQuantity ? (
            <span aria-hidden className="text-sm px2">
              {currentQuantity}
            </span>
          ) : null}
          {!maximumModifierAmountReached ? (
            <PlusButton
              accessibleLabelId={accessibleLabelId}
              currentQuantity={currentQuantity}
              defaultQuantity={defaultQuantity}
              inRecipe={inRecipe}
              modifiedFromDefault={modifiedFromDefault}
              onClick={buttonClickHandler}
              onValueChange={onValueChange}
              productOptionName={productOptionName}
              ref={plusButtonRef}
            />
          ) : (
            <div style={{ width: '24px' }} />
          )}
        </div>
      </FieldBase>
    </div>
  );
};

Stepper.propTypes = {
  currentQuantity: PropTypes.any,
  defaultQuantity: PropTypes.number,
  inRecipe: PropTypes.bool,
  isRemovable: PropTypes.bool,
  onValueChange: PropTypes.func,
  productOptionForm: PropTypes.object,
  productOptionName: PropTypes.string,
  topLevelCategoryName: PropTypes.string,
  unit: PropTypes.string,
};

export default Stepper;
